import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, User, ArrowLeft, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function RegisterPharmacy({ onBackToHome, onNavigateToLogin }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Pharmacie
    pharmacy_name: '',
    registration_number: '',
    address: '',
    phone: '',
    // Propriétaire
    owner_username: '',
    owner_password: '',
    owner_full_name: '',
    owner_phone: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.owner_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register-pharmacy`, formData);
      
      setSuccess(true);
      toast.success(response.data.message || 'Pharmacie créée avec succès !');
      
      // Redirect to home after 3 seconds
      setTimeout(() => {
        if (onBackToHome) {
          onBackToHome();
        }
      }, 3000);
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">Inscription Réussie !</CardTitle>
            <CardDescription>
              Votre demande a été envoyée avec succès.<br />
              Votre compte sera activé manuellement par l'administrateur de la plateforme. Aucun email ne sera envoyé.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Vous pourrez vous connecter une fois votre pharmacie validée.
            </p>
            <Button 
              onClick={onBackToHome}
              className="w-full bg-gradient-to-r from-[#004d3d] to-[#00ff7f]"
            >
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" onClick={onBackToHome}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
          <CardTitle className="text-2xl md:text-3xl">Inscription Pharmacie</CardTitle>
          <CardDescription>
            Créez votre compte pharmacie et commencez à gérer votre comptabilité
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section Pharmacie */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center text-green-700">
                <Building2 className="w-5 h-5 mr-2" />
                Informations Pharmacie
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="pharmacy_name">Nom de la Pharmacie *</Label>
                  <Input
                    id="pharmacy_name"
                    name="pharmacy_name"
                    value={formData.pharmacy_name}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Pharmacie Centrale"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="registration_number">Registre de Commerce</Label>
                  <Input
                    id="registration_number"
                    name="registration_number"
                    value={formData.registration_number}
                    onChange={handleChange}
                    placeholder="Ex: RC123456"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+213 555 123 456"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Rue de la Pharmacie, Alger"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Section Propriétaire */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-700">
                <User className="w-5 h-5 mr-2" />
                Votre Compte Administrateur
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="owner_full_name">Nom Complet *</Label>
                  <Input
                    id="owner_full_name"
                    name="owner_full_name"
                    value={formData.owner_full_name}
                    onChange={handleChange}
                    required
                    placeholder="Dr. Ahmed Benali"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="owner_username">Nom d'Utilisateur *</Label>
                  <Input
                    id="owner_username"
                    name="owner_username"
                    value={formData.owner_username}
                    onChange={handleChange}
                    required
                    placeholder="ahmed_benali"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="owner_password">Mot de Passe *</Label>
                  <Input
                    id="owner_password"
                    name="owner_password"
                    type="password"
                    value={formData.owner_password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    placeholder="Min. 6 caractères"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="owner_phone">Votre Téléphone</Label>
                  <Input
                    id="owner_phone"
                    name="owner_phone"
                    type="tel"
                    value={formData.owner_phone}
                    onChange={handleChange}
                    placeholder="+213 555 987 654"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#004d3d] to-[#00ff7f] hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-6"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Création en cours...
                </div>
              ) : (
                <>
                  <Building2 className="w-5 h-5 mr-2" />
                  Créer ma Pharmacie
                </>
              )}
            </Button>

            <p className="text-center text-sm text-gray-600">
              Vous avez déjà un compte ?{' '}
              <button 
                type="button"
                onClick={onNavigateToLogin} 
                className="text-green-600 hover:underline font-semibold"
              >
                Se connecter
              </button>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default RegisterPharmacy;
